<template>
    <grid ref="grid" :column-defs="columnDefs" :fetch-callback="fetchCallback">
        <date-range-picker
            :range-options="predefinedRanges"
            :value="dateRange"
            :date-format="userUnicodeDateFormat"
            @input="handleDaterangeChange"
        ></date-range-picker>
        <b-button-group aria-label="Export table data">
            <responsive-button
                :icon="['fas', 'file-excel']"
                :href="exportExcelHref"
                @click="generatingExcelReportSuccessToast"
                variant="primary"
            >
            </responsive-button>
        </b-button-group>
        <responsive-button
            :icon="['fas', 'arrows-rotate']"
            variant="primary"
            @click="refresh"
            >Refresh</responsive-button
        >
    </grid>
</template>

<script>
import { mapGetters } from 'vuex';
import format from 'date-fns/format';
import subYears from 'date-fns/subYears';
import API from '../../../api';
import {
    DAMAGE_PROTECTION_CLAIM_EXCEL_ENDPOINT,
    DAMAGE_PROTECTION_CLAIM_GRID_ENDPOINT,
} from '../../../endpoints';
import DateRangePicker, {
    ALL_DATES,
    CUSTOM_RANGE,
    LAST_MONTH,
    LAST_YEAR,
    THIS_MONTH,
    THIS_YEAR,
    TODAY,
    YESTERDAY,
} from '../../generic/date-range-picker/date-range-picker.vue';
import Grid from '../../generic/grid/grid.vue';
import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';

export default {
    name: 'DamageProtectionClaims',
    components: {
        DateRangePicker,
        Grid,
        ResponsiveButton,
    },
    computed: {
        ...mapGetters('header', [
            'userCurrencySymbol',
            'userUnicodeDateFormat',
        ]),
        fetchCallback() {
            return ({ offset, limit, sort, desc, search }) => {
                this.offset = offset;
                this.limit = limit;
                this.sort = sort;
                this.desc = desc;
                this.search = search;
                return API.get(DAMAGE_PROTECTION_CLAIM_GRID_ENDPOINT(), {
                    params: {
                        offset,
                        limit,
                        sort,
                        desc,
                        search,
                        from_date: this.fromDateRequest,
                        to_date: this.toDateRequest,
                    },
                });
            };
        },
        exportExcelHref() {
            return (
                `${DAMAGE_PROTECTION_CLAIM_EXCEL_ENDPOINT()}` +
                `?offset=${this.offset}` +
                `&limit=${this.limit}` +
                `&sort=${this.sort}` +
                `&desc=${this.desc}` +
                `&search=${this.search}` +
                `&from_date=${this.fromDateRequest}` +
                `&to_date=${this.toDateRequest}`
            );
        },
        fromDateRequest() {
            return format(this.dateRange.from, this.dateRequestFormat);
        },
        toDateRequest() {
            return format(this.dateRange.to, this.dateRequestFormat);
        },
    },
    data() {
        return {
            columnDefs: [
                {
                    field: 'number',
                    headerName: 'ID',
                    sort: 'desc',
                },
                {
                    field: 'status',
                    headerName: 'Status',
                    minWidth: 280,
                },
                {
                    field: 'policy_type',
                    headerName: 'Policy',
                    sortable: false,
                },
                {
                    field: 'full_name',
                    headerName: 'Insured',
                },
                {
                    field: 'age',
                    headerName: 'Age',
                },
                {
                    field: 'submitted',
                    headerName: 'Opened',
                    type: 'dateColumn',
                },
                {
                    field: 'amount',
                    headerName: 'Amount',
                    cellRenderer: ({ value }) => {
                        if (!isNaN(value)) {
                            const number = Number(value).toFixed(2);
                            if (number > 0) {
                                return `${this.userCurrencySymbol}${number}`;
                            } else if (number < 0) {
                                return `-${this.userCurrencySymbol}${-number}`;
                            } else if (number == 0) {
                                return `Pending`;
                            }
                        }
                    },
                },
                {
                    headerName: '',
                    sortable: false,
                    maxWidth: 145,
                    minWidth: 145,
                    resizable: false,
                    sortable: false,
                    cellRenderer: ({ data }) => {
                        return `<a class="btn btn-primary" target="_self" title="View Claim" href="${data.view_link}">View Claim</a>`;
                    },
                },
            ],
            dateRange: {
                name: CUSTOM_RANGE,
                from: subYears(new Date(), 1),
                to: new Date(),
            },
            dateRequestFormat: 'yyyy-MM-dd',
            predefinedRanges: [
                TODAY,
                YESTERDAY,
                THIS_MONTH,
                LAST_MONTH,
                THIS_YEAR,
                LAST_YEAR,
                ALL_DATES,
            ],
            offset: 0,
            limit: 20,
            sort: 'id',
            desc: false,
            search: '',
        };
    },
    methods: {
        handleDaterangeChange(value) {
            this.dateRange = { ...value };
            this.refresh();
        },
        refresh() {
            this.$refs.grid.refreshDataSource();
        },
    },
};
</script>
